import { useState } from 'react';
import { mediaUrl } from '../lib/api';

/**
 * Hearing a voice is the product.
 *
 * Playback used to be `new Audio(url).play()` — no controls, no progress, no
 * pause, and silence whether the recording played or failed. For a grandchild
 * listening to a grandmother who has died, "nothing happened" is the worst
 * possible answer. So: a real player, loaded only when asked (the download link
 * is short-lived and private), with a plain message if it cannot be fetched.
 */
export function VoicePlayer({ mediaAssetId, label = 'Play recording' }: { mediaAssetId: string; label?: string }) {
  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (url) {
    return (
      <div className="voice-player stack tight">
        {/* eslint-disable-next-line jsx-a11y/media-has-caption */}
        <audio controls autoPlay src={url} onError={() => setError('That recording would not play. It is still safely stored — please try again.')}>
          Your browser cannot play audio.
        </audio>
        {error && (
          <span className="small" role="alert">
            {error}
          </span>
        )}
      </div>
    );
  }

  return (
    <div className="stack tight">
      <div>
        <button
          className="btn small ghost"
          disabled={loading}
          onClick={async () => {
            setLoading(true);
            setError(null);
            try {
              setUrl(await mediaUrl(mediaAssetId));
            } catch {
              setError('We could not fetch that recording just now. Nothing has been lost — please try again.');
            } finally {
              setLoading(false);
            }
          }}
        >
          {loading ? 'Fetching…' : `▶ ${label}`}
        </button>
      </div>
      {error && (
        <span className="small" role="alert">
          {error}
        </span>
      )}
    </div>
  );
}
